// ═══════════════════════════════════════════════════════════════════════════════
// 222fasting — State Module v2.0
// Single source of truth for app state
// ═══════════════════════════════════════════════════════════════════════════════

import { createClient } from '@supabase/supabase-js';

// ─── Environment Variables (injected by Vite at build time) ────────────────────
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY;
const API_BASE_URL = import.meta.env.VITE_API_BASE;

// ─── Supabase ───────────────────────────────────────────────────────────────────
if (!SUPABASE_URL || !SUPABASE_ANON) {
  console.error('[222fasting] Missing env vars — create .env.local with VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

export const sb = (SUPABASE_URL && SUPABASE_ANON)
  ? createClient(SUPABASE_URL, SUPABASE_ANON)
  : null;

// ─── API ────────────────────────────────────────────────────────────────────────
export const API_BASE = API_BASE_URL;

// ─── Environment Detection ──────────────────────────────────────────────────────
export const isDev = import.meta.env.DEV || window.location.hostname.includes('222fasting.co.za');


// ─── LocalStorage ───────────────────────────────────────────────────────────────
export const Storage = {
  prefix: '222f_',
  
  get(key) {
    try {
      const raw = localStorage.getItem(this.prefix + key);
      if (raw === null) return null;
      try { return JSON.parse(raw); } catch { return raw; }
    } catch { return null; }
  },
  
  set(key, value) {
    try {
      if (value === null || value === undefined) {
        localStorage.removeItem(this.prefix + key);
      } else if (typeof value === 'object') {
        localStorage.setItem(this.prefix + key, JSON.stringify(value));
      } else {
        localStorage.setItem(this.prefix + key, String(value));
      }
      return true;
    } catch { return false; }
  },
  
  getBool(key, defaultValue = false) {
    const value = this.get(key);
    if (value === null) return defaultValue;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value === 'true' || value === '1';
    return Boolean(value);
  },
  
  remove(key) {
    try { localStorage.removeItem(this.prefix + key); return true; } catch { return false; }
  }
};

// ─── Fasting Phases ─────────────────────────────────────────────────────────────
export const phases = [
  { name: 'Digestion',     icon: '🔋', start: 0,  end: 4,  desc: 'Last meal processing' },
  { name: 'Glycogen Burn', icon: '🍯', start: 4,  end: 12, desc: 'Liver glycogen depleting' },
  { name: 'Fat Burning',   icon: '🔥', start: 12, end: 18, desc: 'Ketones rising' },
  { name: 'Autophagy',     icon: '🧹', start: 18, end: 24, desc: 'Cell cleaning active' },
  { name: 'Deep Ketosis',  icon: '🧠', start: 24, end: 48, desc: 'Peak fat adaptation' },
  { name: 'Immune Reset',  icon: '⚡', start: 48, end: 72, desc: 'Stem cell surge' },
];

// ─── Protocol Info ───────────────────────────────────────────────────────────────
export const protocolInfo = {
  '16:8':  { label: 'Daily Metabolic',        color: '#c9a84c', note: '16h fast · 8h eat · Daily reset' },
  '18:6':  { label: 'Accelerated Fat Burn',   color: '#4caf7d', note: '18h fast · 6h eat · Deeper ketosis' },
  '20:4':  { label: 'Warrior Protocol',       color: '#e8772e', note: '20h fast · 4h eat · Advanced' },
  'OMAD':  { label: 'One Meal A Day',         color: '#e94560', note: '23h fast · 1h eat · Extreme daily' },
  'OMAW':  { label: 'One Meal A Week',        color: '#9b59b6', note: 'Deep obesity reversal · Paid' },
  'TMAW':  { label: 'Two Meals A Week',       color: '#3498db', note: 'Bridge protocol · Paid' },
  'OMAM':  { label: 'One Meal A Month',       color: '#e74c3c', note: 'Advanced · Blood tests required' },
  'TMAM':  { label: 'Two Meals A Month',      color: '#e74c3c', note: 'Advanced · Blood tests required' },
};

// ─── Tier Data ───────────────────────────────────────────────────────────────────
export const tierData = {
  'OMAW': {
    tag: 'PAID', tagClass: 'paid',
    title: 'One Meal A Week', label: 'Deep Obesity Reversal',
    desc: 'One meal per week. Designed for significant metabolic reset. Medical supervision strongly recommended.',
    warning: 'Blood tests every 4 weeks minimum. <strong>Potassium, Albumin, and Insulin must be monitored.</strong>'
  },
  'TMAW': {
    tag: 'PAID', tagClass: 'paid',
    title: 'Two Meals A Week', label: 'Bridge Protocol',
    desc: 'Two to three meals per week. The bridge between OMAD and deeper protocols.',
    warning: 'Medical supervision strongly recommended for protocols beyond 48 hours.'
  },
  'OMAM': {
    tag: 'BLOOD REQUIRED', tagClass: 'blood-required',
    title: 'One Meal A Month', label: 'Advanced Obesity Reversal',
    desc: 'One meal per month. Requires full blood panel on file and medical supervision.',
    warning: 'Blood tests every 2–3 weeks mandatory. <strong>Do not attempt without confirmed medical support.</strong>'
  },
  'TMAM': {
    tag: 'BLOOD REQUIRED', tagClass: 'blood-required',
    title: 'Two Meals A Month', label: 'Advanced Obesity Reversal',
    desc: 'Two to three meals per month. Requires full blood panel on file and medical supervision.',
    warning: 'Blood tests every 2–3 weeks mandatory. <strong>Do not attempt without confirmed medical support.</strong>'
  },
  'IRF': {
    tag: 'APPLICATION ONLY', tagClass: 'application',
    title: 'Insulin Reset Fast', label: 'Highest Tier Protocol',
    desc: 'Continuous fast until fasting insulin drops below 6 mIU/L on two consecutive fortnightly tests. Not a duration — a biochemical mission.',
    warning: '<strong>Personal supervision by Francois required.</strong> Entry requires full blood panel, doctor approval, and personal admission. Do not attempt without authorization.'
  }
};

console.log('[222fasting] Environment:', isDev ? 'DEV' : 'PROD');
console.log('[222fasting] API:', API_BASE);

// ─── Module Exports ─────────────────────────────────────────────────────────────
// weight.js removed - these functions are now in WeightContext.tsx
